import { API_BASE_URL } from '@synergy/config';
import { Button } from '@mui/material';
import { DocumentStatus, DocumentType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { FormikErrors } from 'formik';
import {
    PaloDetailView,
    PaloDocumentPreview,
    PaloHistory,
    PaloRecordPager,
    PaloStatusPill,
    PaloStep,
    PaloTabs,
} from '@synergy/components-palo';
import { PaloNextThemeProvider, tokens } from '@synergy/theme-palo';
import { customerMediaSelector, internalMediaSelector } from '@synergy/data-access';
import { getDocumentStatusTranslationId, hasErrors, padZeroLeft } from '@synergy/utils';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { ConverterReceiptModal } from '../../../../convert-receipt/converter-receipt-modal';
import { DemandDetailCtaButton } from '../demand-detail-cta-button';
import { DemandDetailDemandItemQuantityModal } from '../demand-detail-total-demand-item-quantity-modal';
import { DemandSteps } from '../models/demand-steps';
import { DemandTransition } from '../../../../convert-receipt/models/demand/demandTransition';
import { DetailStream } from '../../../../activity-sidebar/details/detail-stream';
import { FormikGroup } from '../../../../../common/components/new-stepper/formik-group';
import { PaloDemandAttachments } from './palo-demand-attachments';
import { PaloDemandDetailActions, PaloDemandDetailMenu } from './palo-demand-detail-actions';
import { PaloDemandDetailFeedback } from './palo-demand-detail-feedback';
import { PaloDemandDetailOverview } from './palo-demand-detail-overview';
import { PaloDemandDetailParticipants } from './palo-demand-detail-participants';
import { PaloDemandDetailPositions } from './palo-demand-detail-positions';
import { PaloDemandDetailTopics } from './palo-demand-detail-topics';
import { PaloDirtyProvider, usePaloStepDirtyLookup } from '../../../../../common/components/palo/palo-dirty-context';
import { PaloDocumentChain, PaloDocumentChainTabName } from '../../../../document-chain/palo-document-chain';
import {
    PaloDocumentChainCreate,
    PaloDocumentChainProvider,
} from '../../../../document-chain/palo-document-chain-context';
import { PaloDocumentNotices } from '../../../../../common/components/palo/palo-document-notices';
import { PaloDocumentTitle } from '../../../../../common/components/palo/palo-document-title';
import { PaloRecordDock } from '../../../../../common/components/palo/palo-record-dock';
import { PaloSaveAction, paloDocumentPhase } from '../../../../detail-header/palo-main-action';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { ReceiptNoteType } from '../../../../../common/models/enums/receipt-note-type';
import {
    Reset,
    deleteDemand,
    fetchDemandTransitionData,
    fetchTotalQuantityPositions,
    saveAll,
} from '../store/demand-detail-actions';
import { Transition } from '../../../../convert-receipt/models/type-transition';
import { TransitionTarget } from '../../../../convert-receipt/models/internal/transition-target';
import { demandDetailOverviewDataSelector } from '../store/overview/demand-detail-overview-selectors';
import { fetchActivities, resetActivities } from '../../../../../common/actions/activity-actions';
import { fetchAllTopics } from '../../../../configuration/organization/topics/store/topics-actions';
import { fetchDemandPositions } from '../store/positions/demand-detail-positions-actions';
import { fetchOverview } from '../store/overview/demand-detail-overview-actions';
import { fetchParticipants } from '../store/participants/demand-detail-participants-actions';
import {
    fetchReceiptInternalNotes,
    resetReceiptInternalNotes,
} from '../../../../../common/actions/internal-notes-actions';
import { fetchTopics } from '../store/topics/demand-detail-topics-actions';
import { fetchTransitions, resetTransitions } from '../../../../../common/actions/transitions-activity-actions';
import { resetSelectedLanguage } from '../../../../../common/actions/selected-language-actions';
import { totalQuantityDemandPositionsSelector, transitionDataSelector } from '../store/demand-detail-selectors';
import {
    transitionDemandIntoInquiries,
    transitionDemandIntoPurchaseOrders,
} from '../../../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const STEP_LABELS: { stepId: DemandSteps; labelId: string }[] = [
    // The positions are part of the overview tab: one page for what a survey
    // asks, filled in from top to bottom (#2253).
    { stepId: DemandSteps.Overview, labelId: 'app.demands.step.overview' },
    { stepId: DemandSteps.Topics, labelId: 'app.demands.step.topics' },
    { stepId: DemandSteps.Participants, labelId: 'app.demands.step.participants' },
    { stepId: DemandSteps.Feedback, labelId: 'app.demands.step.feedback' },
];

const PaloDemandDetailInner = () => {
    const stepDirty = usePaloStepDirtyLookup();
    const dispatch = useThunkDispatch();
    const [activeStep, setActiveStep] = useState(DemandSteps.Overview);
    const [transitionModalOpen, setTransitionModalOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [isOpenTotalDemandQuantityModal, setIsOpenTotalDemandQuantityModal] = useState(false);
    const transitionData = useAppSelector(transitionDataSelector);
    const internalMedia = useAppSelector(internalMediaSelector);
    const customerMedia = useAppSelector(customerMediaSelector);
    const navigate = useNavigate();
    const idParamString = useParams<{ id: string }>();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const idParam = +idParamString.id!;
    const demandDetail = useAppSelector(demandDetailOverviewDataSelector);
    const demandHead = useAppSelector(state => state.demandDetail.overview.stepData.values);
    const totalQuantityDemandPositions = useAppSelector(totalQuantityDemandPositionsSelector);
    const unlocked = useAppSelector(state => state.demandDetail.common.unlocked);
    const { id: demandId } = demandDetail.values;
    const phase = paloDocumentPhase(demandHead.status, { saved: !!demandHead.id, unlocked });

    useEffect(() => {
        if (demandId && !idParam) {
            navigate(`/demands/${demandId}`, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demandId, navigate]);

    useEffect(() => {
        const initializeFunction = async () => {
            if (idParam) {
                dispatch(fetchActivities(DocumentType.Demand, idParam));
                dispatch(fetchTransitions(DocumentType.Demand, idParam));
                dispatch(fetchReceiptInternalNotes(ReceiptNoteType.Demands, idParam));
                await dispatch(fetchOverview(idParam));
                dispatch(fetchDemandPositions());
                dispatch(fetchAllTopics());
                dispatch(fetchTopics());
                dispatch(fetchParticipants());
                dispatch(fetchTotalQuantityPositions());
            }
        };

        initializeFunction();
    }, [dispatch, idParam]);

    useEffect(() => {
        dispatch(resetSelectedLanguage());

        return () => {
            dispatch(Reset());
            dispatch(resetActivities());
            dispatch(resetTransitions());
            dispatch(resetReceiptInternalNotes());
        };
    }, [dispatch]);

    const ref = useRef<{
        submitAll: () => void;
        submit: () => Promise<FormikErrors<unknown>>;
        validate: () => Promise<FormikErrors<unknown>>;
    }>();

    const handleSave = async () => {
        await ref.current?.submit();
        await dispatch(saveAll());
        if (idParam) {
            dispatch(fetchActivities(DocumentType.Demand, idParam));
            dispatch(fetchReceiptInternalNotes(ReceiptNoteType.Demands, idParam));
        }
    };

    const handleStepChange = async (step: number, save = false): Promise<void> => {
        const errors = await ref.current?.submit();

        // Always allow backward stepping
        if (step < activeStep) {
            setActiveStep(step);
            return;
        }

        // Disallow next step if there are currently any errors
        if (!errors || hasErrors(errors)) {
            return;
        }

        if (save && !(await dispatch(saveAll()))) {
            return;
        }
        if (save && idParam) {
            dispatch(fetchActivities(DocumentType.Demand, idParam));
            dispatch(fetchReceiptInternalNotes(ReceiptNoteType.Demands, idParam));
        }
        setActiveStep(step);
    };

    const handleDelete = async () => {
        await dispatch(deleteDemand());
        navigate(-1);
    };

    const handleTransition = () => {
        dispatch(fetchDemandTransitionData());
        setTransitionModalOpen(true);
    };

    const handleConvert = (transitionObject: Transition, target: TransitionTarget) =>
        TransitionTarget.PurchaseOrder === target
            ? dispatch(transitionDemandIntoPurchaseOrders(transitionObject as DemandTransition))
            : dispatch(transitionDemandIntoInquiries(transitionObject as DemandTransition));

    const steps: PaloStep[] = [
        ...STEP_LABELS.map(
            ({ stepId, labelId }): PaloStep => ({
                label: <FormattedMessage id={labelId} />,
                state: stepId === activeStep ? 'now' : stepId < activeStep ? 'done' : 'todo',
                // The step's own mark, not "the wizard has something unsaved and
                // this is the open step". The open step is the one a reader
                // needs telling about least; the marker is there to say that
                // something is waiting on a step they have left (#2159).
                //
                // A step keeps its green check while it carries the dot: the two
                // say different things — green is "you have been here", orange is
                // "this is not saved" — and a step can be both.
                dirty: stepDirty(stepId) || (DemandSteps.Overview === stepId && stepDirty(DemandSteps.Positions)),
                onClick: () => handleStepChange(stepId),
            })
        ),
        {
            label: <PaloDocumentChainTabName />,
            state: DemandSteps.Documents === activeStep ? 'now' : 'todo',
            // Parks what the open step holds, then shows the chain. It does not
            // go through handleStepChange: that refuses to move on while the
            // form has errors, and a tab that only reads must not be locked
            // away behind a field someone still has to fill in.
            onClick: async () => {
                await ref.current?.submit();
                setActiveStep(DemandSteps.Documents);
            },
        },
    ];

    // The tab counts what it holds, like the two beside it.
    const attachmentCount = internalMedia.length + customerMedia.length;

    const dock = <PaloRecordDock count={attachmentCount} attachments={<PaloDemandAttachments />} />;

    // Nothing to look back on before the first save.
    const history = idParam ? (
        <PaloHistory>
            <DetailStream documentType={DocumentType.Demand} documentId={idParam} />
        </PaloHistory>
    ) : undefined;

    return (
        <>
            {/* The tab offers the two follow-ups a demand has; the data the
                conversion needs is fetched when someone picks one, the same way
                the header button does it (#2166). */}
            <PaloDocumentChainCreate
                source={transitionData ?? undefined}
                targets={[TransitionTarget.Inquiry, TransitionTarget.PurchaseOrder]}
                prepare={() => dispatch(fetchDemandTransitionData())}
                onConvert={handleConvert}
            />
            <PaloDocumentTitle
                typeId="app.document.demand"
                number={demandHead.id ? padZeroLeft(demandHead.id, 5) : undefined}
            />
            {/* FRM-18: which action is the filled one follows the state of the demand. */}
            <PaloDetailView
                themeContent={false}
                onBack={() => navigate(ROUTES.demands)}
                number={demandHead.id ? padZeroLeft(demandHead.id, 5) : undefined}
                title={<FormattedMessage id="app.document.demand" />}
                status={
                    demandHead.status ? (
                        <PaloStatusPill
                            status={demandHead.status}
                            label={<FormattedMessage id={getDocumentStatusTranslationId(demandHead.status)} />}
                        />
                    ) : undefined
                }
                tabs={<PaloTabs steps={steps} />}
                breadcrumbs={[
                    {
                        label: <FormattedMessage id="app.navigation.demands" />,
                        onClick: () => navigate(ROUTES.demands),
                    },
                ]}
                pager={<PaloRecordPager currentId={demandHead.id} onOpen={id => navigate(`${ROUTES.demands}/${id}`)} />}
                primaryAction={<PaloSaveAction phase={phase} comparesToServer={true} onSave={handleSave} />}
                menu={<PaloDemandDetailMenu onDelete={handleDelete} onPreview={() => setPreviewOpen(true)} />}
                // The summary: the one of the three lists that shows the whole
                // survey (#2316).
                preview={{
                    onOpen: () => setPreviewOpen(true),
                    disabled: !demandHead.id || DocumentStatus.Created === demandHead.status,
                    disabledHint: <FormattedMessage id="app.demands.previewAfterStart" />,
                }}
                actions={
                    <PaloDemandDetailActions
                        phase={phase}
                        onTransition={handleTransition}
                        flowActions={
                            <>
                                {activeStep === DemandSteps.Feedback && (
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        size="small"
                                        sx={{ color: tokens.color.ink2 }}
                                        onClick={() => {
                                            dispatch(fetchTotalQuantityPositions());
                                            setIsOpenTotalDemandQuantityModal(true);
                                        }}
                                    >
                                        <FormattedMessage id="app.demands.detail.totalDemandItemQuantity" />
                                    </Button>
                                )}
                                <DemandDetailCtaButton />
                            </>
                        }
                    />
                }
                dock={idParam ? dock : undefined}
                history={history}
                alert={<PaloDocumentNotices />}
            >
                <PaloNextThemeProvider>
                    <FormikGroup ref={ref} activeStep={activeStep} stepperState={{ isNew: true }}>
                        {activeStep === DemandSteps.Overview && (
                            <PaloDemandDetailOverview positions={<PaloDemandDetailPositions />} />
                        )}
                        {activeStep === DemandSteps.Topics && <PaloDemandDetailTopics />}
                        {activeStep === DemandSteps.Participants && <PaloDemandDetailParticipants />}
                        {activeStep === DemandSteps.Feedback && <PaloDemandDetailFeedback demandId={demandId} />}
                        {activeStep === DemandSteps.Documents && <PaloDocumentChain />}
                    </FormikGroup>
                </PaloNextThemeProvider>
            </PaloDetailView>

            <DemandDetailDemandItemQuantityModal
                open={isOpenTotalDemandQuantityModal}
                onClose={() => setIsOpenTotalDemandQuantityModal(false)}
                translationId={'app.demands.detail.totalDemandItemQuantity'}
                demandItems={totalQuantityDemandPositions}
            />

            <PaloDocumentPreview
                url={
                    previewOpen && demandHead.id
                        ? urlJoin(API_BASE_URL, `/demands/${demandHead.id}/pdf-overview?listType=SUMMARY`)
                        : undefined
                }
                title={<FormattedMessage id="app.common.preview" />}
                openLabel={<FormattedMessage id="app.common.openInNewTab" />}
                closeLabel={<FormattedMessage id="app.common.close" />}
                onClose={() => setPreviewOpen(false)}
            />
            {transitionData && (
                <ConverterReceiptModal
                    isVisible={transitionModalOpen}
                    source={transitionData}
                    onClose={() => setTransitionModalOpen(false)}
                    onConvert={handleConvert}
                />
            )}
        </>
    );
};

export const PaloDemandDetail = () => {
    // A fresh demand from the server is the one moment at which every step,
    // including the four that are not mounted, is provably saved. Each step
    // fetches its own part, so no step may decide this for the others.
    const loadedDocument = useAppSelector(state => state.demandDetail.overview.baseline);

    return (
        <PaloDirtyProvider loadedDocument={loadedDocument}>
            <PaloDocumentChainProvider documentType={DocumentType.Demand}>
                <PaloDemandDetailInner />
            </PaloDocumentChainProvider>
        </PaloDirtyProvider>
    );
};
